import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './contexts/AuthContext';
import { CoreDataProvider } from './contexts/CoreDataContext';
import { SearchProvider } from './components/SearchProvider';
import { CoreDataLoader } from './components/CoreDataLoader';
import { useDarkMode } from './hooks/useDarkMode';
import AppRoutes from './router';
import { loadConfig } from './lib/config';
import { LoadingScreen } from './components/LoadingScreen';
import ToastContainerWrapper from './components/ToastContainerWrapper';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  
  // Initialize dark mode based on config
  useDarkMode();

  useEffect(() => {
    loadConfig()
      .then(() => {
        setConfigLoaded(true);
      })
      .catch((error) => {
        console.error('Failed to load configuration:', error);
        setConfigError('Failed to load application configuration. Please check your config.json file.');
      });
  }, []);

  if (configError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-avemio-dark-bg">
        <div className="max-w-md rounded-lg bg-white dark:bg-avemio-dark-surface p-8 shadow-lg border border-gray-200 dark:border-avemio-dark-border">
          <h1 className="mb-4 text-2xl font-bold text-red-600 dark:text-red-400">Configuration Error</h1>
          <p className="text-gray-700 dark:text-avemio-dark-text-primary">{configError}</p>
        </div>
      </div>
    );
  }

  if (!configLoaded) {
    return <LoadingScreen message="Loading configuration..." />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <CoreDataProvider>
            <CoreDataLoader>
              <SearchProvider>
                <AppRoutes />
                <ToastContainerWrapper />
              </SearchProvider>
            </CoreDataLoader>
          </CoreDataProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;

